import { useCallback, useEffect, useState } from 'react'

export type Theme = 'light' | 'dark'

const KEY = 'theme'

function stored(): Theme | null {
  try {
    const value = localStorage.getItem(KEY)
    return value === 'light' || value === 'dark' ? value : null
  } catch {
    // Storage can be unavailable. The system preference still applies, so
    // there is nothing to report.
    return null
  }
}

function systemPrefersDark(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches
}

/**
 * Light or dark, with no third "system" state.
 *
 * The system preference is what applies until someone chooses, which is the
 * same behaviour with one less control state to explain. A choice is stored and
 * applied as `data-theme` on the document element, which the stylesheet reads.
 */
export function useTheme() {
  const [theme, setTheme] = useState<Theme>(() =>
    stored() ?? (systemPrefersDark() ? 'dark' : 'light'),
  )

  useEffect(() => {
    const root = document.documentElement
    if (stored()) {
      root.dataset.theme = theme
    } else {
      // Nothing chosen yet: leave the attribute off so the media query governs.
      delete root.dataset.theme
    }
  }, [theme])

  const toggle = useCallback(() => {
    setTheme((current) => {
      const next: Theme = current === 'dark' ? 'light' : 'dark'
      try {
        localStorage.setItem(KEY, next)
      } catch {
        // A theme that cannot be remembered still applies for this visit.
      }
      document.documentElement.dataset.theme = next
      return next
    })
  }, [])

  return { theme, toggle }
}
